import { Upload, UploadProps } from 'antd';
import cls from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import styles from './index.module.scss';
import { selectedIcon, addIcon } from '../../../assets/ImageCreation';
import closeIcon from '../../../assets/ImageCreation/close.svg';
import { MAX_IMAGE_SIZE } from '../../../constants/image';
import { useAvatarList } from '../../../hooks/useAvatarList';
import { useAvatarSafeCheck } from '../../../hooks/useSafeCheck';
import { useAppDispatch, useAppSelector } from '../../../state';
import { setCurrStyleName, setImgConfig, setImgPreviewExtra } from '../../../state/image';
import { track } from '../../../track';
import { AvatarStyleElem } from '../../../types/material';
import { uploadImageFile } from '../../../utils/upload';
import StepLoading from '../../Components/Loading/step';
import { LocalImage } from '../../Image';

interface StyleReferenceBarProps {
  isShow: boolean;
  cdnPrefix: string;
  setIsShow: (isShow: boolean) => void;
  source: string;
}

export const StyleReferenceBar: React.FC<StyleReferenceBarProps> = ({
  isShow,
  cdnPrefix,
  setIsShow,
  source,
}) => {
  const { authData } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { safeCheck } = useAvatarSafeCheck();
  const [loading, setLoading] = useState(false);
  const { avatar_style_id_list } = useAppSelector((state) => state.image.imgConfig);
  const selectedStyleId = useMemo(() => avatar_style_id_list?.[0] || null, [avatar_style_id_list]);

  const ref = useRef<HTMLDivElement>(null);
  const { avatarStyleList, categoryList, currCategoryId, setCurrCategoryId } = useAvatarList();

  const onClickCategory = (categoryId?: number) => {
    const oldCategoryId = currCategoryId;
    if (oldCategoryId === categoryId) return;
    if (categoryId === undefined) return;
    setCurrCategoryId(categoryId);
  };

  const onClickStyle = (item: AvatarStyleElem) => {
    if (!item.avatar_style_id) return;

    dispatch(
      setImgConfig({
        avatar_style_id_list: [item.avatar_style_id],
      })
    );
    dispatch(setCurrStyleName(item.style_show_name || ''));
    dispatch(setImgPreviewExtra({ style_img_url: item.avatar_style_img_url }));
    track.send('style_reference_choice_click', source, {
      style_reference_id: item.avatar_style_id,
    });
    setIsShow(false);
  };

  const props: UploadProps = useMemo(
    () => ({
      name: 'file',
      accept: 'image/png,image/jpg,image/jpeg',
      customRequest: async (options) => {
        const { file } = options;
        if (!file) return;
        if ((file as File).size > MAX_IMAGE_SIZE) {
          toast.error(t('upload_image_size_exceed'));
          return;
        }
        setLoading(true);

        try {
          const { pathname } = await uploadImageFile({
            userId: authData.userId,
            file: file as File,
          });

          const url = cdnPrefix + pathname;

          const pass = await safeCheck(url);
          if (pass.length === 0 || pass.includes(false)) throw new Error(t('image_not_pass'));

          dispatch(setImgPreviewExtra({ style_img_url: url }));
          track.send('style_reference_image_upload_success', source, {
            image_url: url,
          });
          // 清掉画风 id
          dispatch(setImgConfig({ avatar_style_id_list: [] }));
          dispatch(setCurrStyleName(''));
          setLoading(false);
          setIsShow(false);
        } catch (error: any) {
          toast.error(error.message || t('upload_image_failed'));
          console.error('upload image failed', error);
          setLoading(false);
        }
      },
      itemRender: () => null,
    }),
    [authData.userId, cdnPrefix, dispatch, safeCheck, setIsShow, t, source]
  );

  const handleUploadClick = () => {
    track.send('style_reference_image_upload_click', source);
  };

  if (!isShow) return null;

  return (
    <div className={styles.styleReferenceBar}>
      <div className={styles.header}>
        <div className={styles.title}>{t('select_style_reference')}</div>
        <img
          className={styles.closeIcon}
          src={closeIcon.src}
          alt="close icon"
          onClick={() => setIsShow(false)}
        />
      </div>
      <Upload {...props} disabled={loading} className={styles.upload}>
        <div className={styles.uploadImage} onClick={handleUploadClick}>
          <img src={addIcon.src} alt="upload image" style={{ opacity: loading ? 0 : 1 }} />
          <div className={styles.uploadImageText} style={{ opacity: loading ? 0 : 1 }}>
            {t('upload_style_reference')}
          </div>
          {loading && (
            <div className={styles.loading}>
              <StepLoading />
            </div>
          )}
        </div>
      </Upload>
      <div className={styles.stylePreset}>
        <div className={styles.styleListTitle}>
          <div className={styles.divider} />
          {t('style_preset')}
          <div className={styles.divider} />
        </div>
        {/* <div className={styles.styleTags}>
          {categoryList.map((category) => (
            <div
              className={cls(styles.styleTag, {
                [styles.active]: currCategoryId === category.category_id,
              })}
              key={category.category_id}
              onClick={() => onClickCategory(category.category_id)}
            >
              {category.category_name}
            </div>
          ))}
        </div> */}
        <div className={styles.styleList} ref={ref}>
          {avatarStyleList.map((item, index) => (
            <div
              className={styles.styleItem}
              key={item.avatar_style_id}
              onClick={() => onClickStyle(item)}
            >
              <img
                className={cls(styles.styleImage, {
                  [styles.selected]: selectedStyleId === item.avatar_style_id,
                })}
                src={item.avatar_style_img_url}
                alt="style preset image"
              />
              {item.style_show_name}
              {selectedStyleId === item.avatar_style_id && (
                <>
                  <div className={styles.mask} />
                  <LocalImage
                    className={styles.selectedIcon}
                    src={selectedIcon}
                    alt="selected icon"
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
